// Territory overlay — makes map control legible on the 3D board.
//
// Two instanced passes over the visibility-filtered tiles:
//  - fill: a soft player-coloured hex tint on every owned LAND tile (the "my
//    area" reading, like a Civ territory tint); water tiles are left clear,
//  - border: a thin player-coloured bar along each edge where the controller
//    differs (unowned tile, another player, or unexplored space).
//
// Pure edge derivation lives in territory.ts; positions use the same world
// projection as the terrain (axialToWorld), and the component sits inside
// the board-scale <group> so Large Board scales it for free.

import type { SceneNode } from '@aod/protocol';
import { playerColor } from '@aod/ui';
import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt } from './elevation.js';
import { WATER_BIOMES } from './terrainEdges.js';
import { borderColorFor, borderDirsOf, edgeMidpointOffset, edgeYaw } from './territory.js';

interface TerritoryOverlayProps {
  readonly tiles: ReadonlyArray<SceneNode>;
  /** Fog-of-war visible coords ("q,r") — territory only renders where visible. */
  readonly visibleCoords?: Set<string> | null;
  readonly biomeMap?: Map<string, string>;
}

const FILL_LIFT = 0.035;
const BORDER_LIFT = 0.055;
const FILL_OPACITY = 0.12;
const BORDER_OPACITY = 0.85;
/** Fraction of the centre→edge-midpoint distance kept for the border bar
 * (the rest insets toward the owner's tile, so opposed borders draw as
 * parallel lines instead of z-fighting on the shared midpoint). */
const BORDER_INSET = 0.1;

export function TerritoryOverlay({ tiles, visibleCoords, biomeMap }: TerritoryOverlayProps) {
  const fillRef = useRef<THREE.InstancedMesh>(null);
  const borderRef = useRef<THREE.InstancedMesh>(null);

  // Owner + dominion-path info over visible tiles — "q,r" → controller.
  const owners = useMemo(() => {
    const map = new Map<string, { ownerId: number; archetype?: string; alignment?: string }>();
    for (const tile of tiles) {
      const q = tile.props?.q as number | undefined;
      const r = tile.props?.r as number | undefined;
      if (q === undefined || r === undefined) continue;
      if (visibleCoords && !visibleCoords.has(`${q},${r}`)) continue;
      // The sea is one continuous surface: a player-coloured hex fill and a hex-edged border floating on it
      // read as a purple hex grid over the water. Land tiles still get their fill and a border along the coast.
      if (WATER_BIOMES.has((tile.props?.biome as string | undefined) ?? 'plains')) continue;
      // `controlledBy` is null for unclaimed land; every player id is a real
      // owner — 0 is the human, whose territory used to be skipped here.
      const owner = tile.props?.controlledBy as number | null | undefined;
      if (owner === null || owner === undefined) continue;
      map.set(`${q},${r}`, {
        ownerId: owner,
        archetype: tile.props?.controllerArchetype as string | undefined,
        alignment: tile.props?.controllerAlignment as string | undefined,
      });
    }
    return map;
  }, [tiles, visibleCoords]);

  // Tile centre lookup for the owned set (world coords, pre-board-scale).
  const centres = useMemo(() => {
    const map = new Map<string, [number, number]>();
    for (const tile of tiles) {
      const q = tile.props?.q as number | undefined;
      const r = tile.props?.r as number | undefined;
      if (q === undefined || r === undefined) continue;
      // Mirrors axialToWorld (x = √3·(q + r/2), z = 1.5·r).
      map.set(`${q},${r}`, [Math.sqrt(3) * (q + r / 2), 1.5 * r]);
    }
    return map;
  }, [tiles]);

  const borderSegs = useMemo(() => {
    const segs: { x: number; y: number; z: number; yaw: number; color: string }[] = [];
    for (const [key, ctrl] of owners) {
      const [q, r] = key.split(',').map(Number) as [number, number];
      const centre = centres.get(key);
      if (!centre) continue;
      const y = (biomeMap ? tileElevationAt(q, r, biomeMap) : 0) + BORDER_LIFT;
      const color = borderColorFor(ctrl.ownerId, ctrl.archetype, ctrl.alignment);
      for (const dir of borderDirsOf(owners, q, r)) {
        const [dq, dr] = HEX_DIR_REFS[dir]!;
        const [ox, oz] = edgeMidpointOffset(dq, dr);
        // Inset toward the owning tile so two adjacent owners draw parallel
        // bars instead of overlapping on the shared midpoint.
        const inset = 1 - BORDER_INSET;
        segs.push({
          x: centre[0] + ox * inset,
          y,
          z: centre[1] + oz * inset,
          yaw: edgeYaw(dq, dr),
          color,
        });
      }
    }
    return segs;
  }, [owners, centres, biomeMap]);

  const fills = useMemo(() => {
    const list: { x: number; z: number; y: number; color: string }[] = [];
    for (const [key, ctrl] of owners) {
      const ownerId = ctrl.ownerId;
      const centre = centres.get(key);
      if (!centre || !biomeMap) continue;
      const [q, r] = key.split(',').map(Number) as [number, number];
      list.push({
        x: centre[0],
        z: centre[1],
        y: tileElevationAt(q, r, biomeMap) + FILL_LIFT,
        color: playerColor(ownerId),
      });
    }
    return list;
  }, [owners, centres, biomeMap]);

  // Write instance matrices + colours. Capacity is fixed at mount (one fill
  // and up to six borders per tile); mesh.count selects the live amount.
  useLayoutEffect(() => {
    const fillsMesh = fillRef.current;
    if (fillsMesh) {
      const _m = new THREE.Matrix4();
      const _c = new THREE.Color();
      for (let i = 0; i < fills.length; i++) {
        const f = fills[i]!;
        _m.makeTranslation(f.x, f.y, f.z);
        fillsMesh.setMatrixAt(i, _m);
        fillsMesh.setColorAt(i, _c.set(f.color));
      }
      fillsMesh.count = fills.length;
      fillsMesh.instanceMatrix.needsUpdate = true;
      if (fillsMesh.instanceColor) fillsMesh.instanceColor.needsUpdate = true;
      fillsMesh.visible = fills.length > 0;
    }
    const borderMesh = borderRef.current;
    if (borderMesh) {
      const _o = new THREE.Object3D();
      const _c = new THREE.Color();
      for (let i = 0; i < borderSegs.length; i++) {
        const s = borderSegs[i]!;
        _o.position.set(s.x, s.y, s.z);
        _o.rotation.set(0, s.yaw, 0);
        _o.updateMatrix();
        borderMesh.setMatrixAt(i, _o.matrix);
        borderMesh.setColorAt(i, _c.set(s.color));
      }
      borderMesh.count = borderSegs.length;
      borderMesh.instanceMatrix.needsUpdate = true;
      if (borderMesh.instanceColor) borderMesh.instanceColor.needsUpdate = true;
      borderMesh.visible = borderSegs.length > 0;
    }
  }, [fills, borderSegs]);

  return (
    <>
      <instancedMesh
        ref={fillRef}
        args={[fillGeo, fillMaterial, Math.max(1, tiles.length)]}
        frustumCulled={false}
        renderOrder={RenderLayer.Infrastructure}
        visible={false}
      />
      <instancedMesh
        ref={borderRef}
        args={[borderGeo, borderMaterial, Math.max(1, tiles.length * 6)]}
        frustumCulled={false}
        renderOrder={RenderLayer.Infrastructure}
        visible={false}
      />
    </>
  );
}

// Shared geometry/material — the meshes are transparent overlays, so depth
// writes stay off and they draw just above the terrain surface.
const fillGeo = new THREE.CircleGeometry(0.97, 6).rotateX(-Math.PI / 2);
const fillMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: FILL_OPACITY,
  depthWrite: false,
});

const borderGeo = new THREE.BoxGeometry(0.94, 0.02, 0.1);
const borderMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: BORDER_OPACITY,
  depthWrite: false,
});

const HEX_DIR_REFS: readonly (readonly [number, number])[] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];
